import React, {useEffect, useState} from 'react';
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  message,
} from 'antd';
import {
  UploadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {applicationConfig} from '../../../Util/config';
import {
  NoEmptySpaceInput,
  OnlyAlphabetInputValidatorWithSpecialCharacters,
  UploadFileMaxSize,
  displayFileName,
  validateEmailInput,
} from '../../../Util/commonUtility';

const isPdfFile = (file) => {
  if (!file) return false;
  const name = file.name || '';
  const type = file.type || file.file_type || '';
  return type === 'application/pdf' || /\.pdf$/i.test(name);
};

const isImageFile = (file) => {
  if (!file) return false;
  if (isPdfFile(file)) return false;
  const name = file.name || '';
  const type = file.type || file.file_type || '';
  return (
    type.startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
  );
};

const buildAssetUrl = (fileName) => {
  if (!fileName || !applicationConfig.UPLOAD_URL) {
    return null;
  }
  return `${applicationConfig.UPLOAD_URL.replace(/\/$/, '')}/${fileName}`;
};

const toUploadFile = (fileRecord, prefix) => {
  if (!fileRecord?.name) {
    return null;
  }
  const id = fileRecord.id;
  const url = buildAssetUrl(fileRecord.name);
  const pdf = isPdfFile(fileRecord);
  return {
    uid: `${prefix}-${id ?? fileRecord.name}`,
    name: displayFileName(fileRecord),
    status: 'done',
    // Only set url for images — picture-card uses url as <img src> and breaks for PDFs.
    url: pdf ? undefined : url,
    fileUrl: url,
    fileId: id,
    type: fileRecord.file_type || (pdf ? 'application/pdf' : undefined),
    file_type: fileRecord.file_type,
  };
};

const buildFileListFromRecord = (files, prefix) => {
  if (Array.isArray(files) && files.length > 0) {
    const uploadFile = toUploadFile(files[0], prefix);
    return uploadFile ? [uploadFile] : [];
  }
  return [];
};

/**
 * Admin modal to edit sponsor contact fields and logo/banner assets.
 * Submits multipart FormData to editEventSponsor (sponsorship/asset/:id).
 */
const AdminSponsorEditModal = ({
  open,
  record,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  const [form] = Form.useForm();
  const [logoFileList, setLogoFileList] = useState([]);
  const [bannerFileList, setBannerFileList] = useState([]);

  useEffect(() => {
    if (!open || !record) {
      return;
    }

    form.setFieldsValue({
      attendee_first_name: record.attendee_first_name || '',
      attendee_last_name: record.attendee_last_name || '',
      attendee_email: record.attendee_email || '',
      attendee_company: record.attendee_company || '',
      event_id: record.event_id,
    });

    setLogoFileList(buildFileListFromRecord(record.logos, 'logo'));
    setBannerFileList(buildFileListFromRecord(record.banners, 'banner'));
  }, [open, record, form]);

  const dummyRequest = ({onSuccess}) => {
    setTimeout(() => onSuccess('ok'), 0);
  };

  const beforeUploadImage = (file, setList) => {
    const isImage = file.type?.startsWith('image/');
    const isLtMax = file.size / 1024 / 1024 < UploadFileMaxSize;
    if (!isImage) {
      message.error('Please upload an image file (png, jpg, jpeg).');
      return Upload.LIST_IGNORE;
    }
    if (!isLtMax) {
      message.error(`Image must be smaller than ${UploadFileMaxSize}MB.`);
      return Upload.LIST_IGNORE;
    }
    setList([
      {
        uid: file.uid || `new-${Date.now()}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
        type: file.type,
        url: URL.createObjectURL(file),
        fileUrl: URL.createObjectURL(file),
      },
    ]);
    return false;
  };

  const beforeUploadBanner = (file) => {
    const isImage = file.type?.startsWith('image/');
    const isPdf =
      file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    const isLtMax = file.size / 1024 / 1024 < UploadFileMaxSize;
    if (!isImage && !isPdf) {
      message.error('Please upload an image or PDF file.');
      return Upload.LIST_IGNORE;
    }
    if (!isLtMax) {
      message.error(`File must be smaller than ${UploadFileMaxSize}MB.`);
      return Upload.LIST_IGNORE;
    }
    const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
    setBannerFileList([
      {
        uid: file.uid || `new-banner-${Date.now()}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
        type: file.type,
        url: previewUrl,
        fileUrl: previewUrl || undefined,
      },
    ]);
    return false;
  };

  const appendExistingOrNewAsset = (
    formData,
    fileList,
    fieldName,
    idsFieldName,
  ) => {
    const file = fileList[0];
    if (!file) {
      formData.append(idsFieldName, '[]');
      return;
    }
    if (file.originFileObj) {
      formData.append(fieldName, file.originFileObj);
      return;
    }
    if (file.fileId) {
      formData.append(idsFieldName, `[${file.fileId}]`);
      return;
    }
    formData.append(idsFieldName, '[]');
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const formData = new FormData();
      formData.append('attendee_first_name', values.attendee_first_name || '');
      formData.append('attendee_last_name', values.attendee_last_name || '');
      formData.append('attendee_email', values.attendee_email || '');
      formData.append('attendee_company', values.attendee_company || '');
      formData.append('event_id', String(values.event_id || record.event_id));

      appendExistingOrNewAsset(formData, logoFileList, 'logos', 'logo_ids');
      appendExistingOrNewAsset(
        formData,
        bannerFileList,
        'banners',
        'banner_ids',
      );

      onSubmit(formData, record.id);
    } catch (err) {
      // Form validation errors are shown inline by antd.
    }
  };

  const renderCurrentAsset = (fileList, label, onClear) => {
    const file = fileList[0];
    if (!file) {
      return null;
    }

    const openUrl = file.fileUrl || file.url;
    const pdf = isPdfFile(file);
    const image = isImageFile(file);

    return (
      <div
        style={{
          marginTop: 8,
          padding: 10,
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          background: '#fafafa',
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            justifyContent: 'space-between',
          }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0}}>
            {pdf ? (
              <FilePdfOutlined style={{fontSize: 28, color: '#cf1322'}} />
            ) : image && openUrl ? (
              <img
                src={openUrl}
                alt={label}
                crossOrigin='anonymous'
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'contain',
                  border: '1px solid #eee',
                  background: '#fff',
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <FileImageOutlined style={{fontSize: 28, color: '#1677ff'}} />
            )}
            <div style={{minWidth: 0}}>
              <div style={{fontSize: 12, color: '#8c8c8c'}}>Current {label}</div>
              {openUrl ? (
                <a
                  href={openUrl}
                  target='_blank'
                  rel='noreferrer'
                  title={file.name}
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                  }}>
                  {file.name}
                </a>
              ) : (
                <span title={file.name}>{file.name}</span>
              )}
              {pdf && (
                <div style={{fontSize: 11, color: '#8c8c8c'}}>PDF document</div>
              )}
            </div>
          </div>
          <Button
            type='text'
            danger
            icon={<DeleteOutlined />}
            onClick={onClear}
            title={`Remove ${label}`}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      title='Edit Logo'
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      centered
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}>
          <Button key='cancel' onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            key='submit'
            type='primary'
            loading={submitting}
            onClick={handleSubmit}>
            Save
          </Button>
        </div>
      }>
      <Form form={form} layout='vertical'>
        <Form.Item name='event_id' hidden>
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name='attendee_first_name'
              label='First Name'
              rules={[
                {required: true, message: 'Please enter first name'},
                {
                  validator: (_, value) =>
                    OnlyAlphabetInputValidatorWithSpecialCharacters(
                      value,
                      'First Name',
                    ),
                },
              ]}>
              <Input placeholder='First name' />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name='attendee_last_name'
              label='Last Name'
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? OnlyAlphabetInputValidatorWithSpecialCharacters(
                          value,
                          'Last Name',
                        )
                      : Promise.resolve(),
                },
              ]}>
              <Input placeholder='Last name' />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name='attendee_email'
              label='Email'
              rules={[
                {required: true, message: 'Please enter email'},
                {validator: (_, value) => validateEmailInput(value, 'Email')},
              ]}>
              <Input placeholder='Email' />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name='attendee_company'
              label='Company'
              rules={[
                {required: true, message: 'Please enter company'},
                {validator: (_, value) => NoEmptySpaceInput(value, 'Company')},
              ]}>
              <Input placeholder='Company' />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{marginTop: 8, marginBottom: 16}} />
        <Typography.Text strong>Assets</Typography.Text>
        <Row gutter={24} style={{marginTop: 12}}>
          <Col xs={24} sm={12}>
            <div style={{marginBottom: 8}}>Company Logo (image)</div>
            {logoFileList.length === 0 ? (
              <Upload
                listType='picture-card'
                accept='image/*'
                maxCount={1}
                fileList={[]}
                customRequest={dummyRequest}
                beforeUpload={(file) =>
                  beforeUploadImage(file, setLogoFileList)
                }
                showUploadList={false}>
                <div>
                  <UploadOutlined />
                  <div style={{marginTop: 4}}>Upload</div>
                </div>
              </Upload>
            ) : (
              renderCurrentAsset(logoFileList, 'logo', () =>
                setLogoFileList([]),
              )
            )}
          </Col>
          <Col xs={24} sm={12}>
            <div style={{marginBottom: 8}}>Banner / Flyer (image or PDF)</div>
            {bannerFileList.length === 0 ? (
              <Upload
                listType='picture-card'
                accept='image/*,.pdf,application/pdf'
                maxCount={1}
                fileList={[]}
                customRequest={dummyRequest}
                beforeUpload={beforeUploadBanner}
                showUploadList={false}>
                <div>
                  <UploadOutlined />
                  <div style={{marginTop: 4}}>Upload</div>
                </div>
              </Upload>
            ) : (
              renderCurrentAsset(bannerFileList, 'banner', () =>
                setBannerFileList([]),
              )
            )}
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AdminSponsorEditModal;
